/**
 * Group requirement strings encode checkbox/radio group validation rules.
 *
 * Format: require_<min>[-<max|ormore>]
 * Examples: "require_1", "require_1-3", "require_2-ormore"
 *
 * Checkbox and radio groups have distinct domains:
 *
 * - **Checkbox groups** use the full grammar: require_<min>[-<max|ormore>].
 *   min ranges from 0 to numFields-1, max from 1 to numFields (where
 *   numFields maps to "ormore"). The editor reads these back with
 *   parseCheckboxRequirement() and writes them with buildCheckboxRequirement().
 *
 * - **Radio groups** are binary: require_1 (required) or require_0-1 (optional).
 *   The API validator (FormFieldGroupValidator) rejects any other value for
 *   radio groups. Use parseRadioRequirement() / buildRadioRequirement().
 *
 * For the signer (untrusted API data), use the strict/safe functions:
 * parseGroupRequirement(), isGroupRequiredSafe(), isValidRequirement().
 *
 * @see SIGN-14073 for the invariant error root cause and editor regex bug.
 */

import { defineMessages, IntlShape } from 'react-intl';
import invariant from 'invariant';

// ---------------------------------------------------------------------------
// Regex patterns (all private — consumers use the typed functions below)
// ---------------------------------------------------------------------------

/** Anchored regex that captures min and optional max from a requirement string. */
const GROUP_REQUIREMENT_PATTERN_STRICT = /^require_(\d+)(?:-(\d+|ormore))?$/;

/** Unanchored regex that captures min and optional max from a requirement string. */
const GROUP_REQUIREMENT_PATTERN = /require_(\d+)(?:-(\d+|ormore))?/;

/** Anchored validation-only pattern (tests format without capturing min). */
const GROUP_REQUIREMENT_VALID = /^require_\d+(?:-(\d+|ormore))?$/;

/**
 * Legacy editor regex with [\d+] bug (matches single digit or '+' literal).
 * Used when sign_core_2026_03_05_fix_editor_group_regex is OFF.
 * @see SIGN-14073
 */
const LEGACY_EDITOR_PATTERN = /require_([\d+])(?:-(\d+|ormore))?/;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GroupRequirement = {
  min: string;
  max: string;
};

export type RadioGroupRequirement = {
  required: boolean;
};

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * Parse a requirement string into {min, max} strings.
 *
 * Uses an unanchored regex (same as the original in use-validation.tsx) so
 * behavior is identical to the pre-extraction version. Throws an invariant
 * on input that doesn't match.
 */
export function parseGroupRequirement(requirement: string): GroupRequirement {
  const match = requirement.match(/require_(\d+)-?(\d+|ormore)?/);
  invariant(
    match,
    `Requirement should match the pattern. Found ${requirement}`,
  );
  const [, min, max = min] = match;
  return { min, max };
}

// ---------------------------------------------------------------------------
// Safe predicates — never throw, usable by editor and both signer apps
// ---------------------------------------------------------------------------

/**
 * Returns true if the requirement string indicates a required group (min > 0).
 * Returns false for invalid/missing values — safe for all callers.
 */
export function isGroupRequiredSafe(requirement: unknown): boolean {
  if (typeof requirement !== 'string' || !requirement) return false;
  const match = requirement.match(GROUP_REQUIREMENT_PATTERN_STRICT);
  if (!match) return false;
  return parseInt(match[1], 10) > 0;
}

/**
 * Returns true if the requirement indicates an optional group (min === 0).
 * Returns false for invalid/missing values — safe for all callers.
 */
export function isGroupOptional(requirement: unknown): boolean {
  return !isGroupRequiredSafe(requirement);
}

/**
 * Returns true if the requirement string is well-formed.
 */
export function isValidRequirement(requirement: unknown): boolean {
  return (
    typeof requirement === 'string' && GROUP_REQUIREMENT_VALID.test(requirement)
  );
}

// ---------------------------------------------------------------------------
// Intl messages used by getGroupValidationInfo
// ---------------------------------------------------------------------------

const groupMessages = defineMessages({
  minOptionsValidationInfo: {
    id: 'c076144cd29a1633e5c5809bce16c1f631f5fff1a07fa0601a2663595d69ba37',
    description:
      'Validation info text for minimum number of options that should be selected for a group of checkboxes',
    defaultMessage:
      'Select min. {min} { min, plural, =1 {option} other {options} }.',
  },
  maxOptionsValidationInfo: {
    id: '3c8dfe313ac86d023beedb69b016307237f771114a5c6dac7fd0ce9e54b006a5',
    description:
      'Validation info text for maximum number of options that can be selected for a group of checkboxes',
    defaultMessage:
      'Select max. {max} { max, plural, =1 {option} other {options} }.',
  },
  exactOptionsValidationInfo: {
    id: 'a4539d03f9cfea1414cee12f182106522fb01e4b170d55433a9c913ece7d2bde',
    description:
      'Validation info text for the exact number of options that should be selected for a group of checkboxes',
    defaultMessage:
      'Select {max} { max, plural, =1 {option} other {options} }.',
  },
  rangeOptionsValidationInfo: {
    id: 'd900461803cf1057475458d89d4ca8ba7bf4f39dd8b231074224fba0d7eb9b48',
    description:
      'Validation info text for the range of options that can be selected for a group of checkboxes',
    defaultMessage: 'Select between {min} and {max} options.',
  },
});

// ---------------------------------------------------------------------------
// Group validation info (checkbox groups only)
// ---------------------------------------------------------------------------

/**
 * Returns a human-readable description of the group requirement for checkbox
 * groups (e.g. "Select min. 2 options."). Used in accessible-document for
 * helper text.
 */
export function getGroupValidationInfo(
  requirement: string,
  intl: IntlShape,
): string | undefined {
  if (!requirement) {
    return;
  }

  const { min, max } = parseGroupRequirement(requirement);

  if (Number(min) === 0) {
    if (max === 'ormore') {
      return;
    } else {
      return intl.formatMessage(groupMessages.maxOptionsValidationInfo, {
        max,
      });
    }
  }

  if (max === 'ormore') {
    return intl.formatMessage(groupMessages.minOptionsValidationInfo, { min });
  }

  if (Number(min) === Number(max)) {
    return intl.formatMessage(groupMessages.exactOptionsValidationInfo, {
      max,
    });
  }

  return intl.formatMessage(groupMessages.rangeOptionsValidationInfo, {
    min,
    max,
  });
}

// ---------------------------------------------------------------------------
// isGroupRequired (throwing variant — matches original behavior)
// ---------------------------------------------------------------------------

/**
 * Returns true if the group's minimum requirement is > 0.
 * Throws on invalid requirement strings (via parseGroupRequirement).
 */
export function isGroupRequired(requirement: string): boolean {
  if (!requirement) {
    return false;
  }

  const { min } = parseGroupRequirement(requirement);

  return Number(min) !== 0;
}

// ---------------------------------------------------------------------------
// Checkbox group — editor parsing and building
// ---------------------------------------------------------------------------

/**
 * Parse a checkbox group requirement string into typed {min, max}.
 *
 * Returns null for null/undefined/non-matching input (never throws).
 * `numFieldsInGroup` is used to resolve "ormore" to a concrete number
 * (the total count of fields in the group).
 * The `fixRegex` flag controls the SIGN-14073 editor regex fix:
 * when true, uses the correct (\d+) pattern; when false, uses the
 * legacy [\d+] pattern for backward compatibility.
 */
export function parseCheckboxRequirement(
  requirement: string | null | undefined,
  numFieldsInGroup: number,
  fixRegex: boolean,
): { min: number; max: number } | null {
  if (requirement === null || requirement === undefined) return null;
  const pattern = fixRegex ? GROUP_REQUIREMENT_PATTERN : LEGACY_EDITOR_PATTERN;
  const match = requirement.match(pattern);
  if (!match) return null;
  const min = parseInt(match[1], 10);
  const maxRaw = match[2] || match[1];
  const max = maxRaw === 'ormore' ? numFieldsInGroup : parseInt(maxRaw, 10);
  return { min, max };
}

/**
 * Build a checkbox group requirement string.
 * When max equals numFieldsInGroup, emits "-ormore" suffix.
 */
export function buildCheckboxRequirement(
  min: number,
  max: number,
  numFieldsInGroup: number,
): string {
  const str = `require_${min}`;
  if (max === numFieldsInGroup) return `${str}-ormore`;
  if (max <= min) return str;
  return `${str}-${max}`;
}

// ---------------------------------------------------------------------------
// Radio group — editor parsing and building
// ---------------------------------------------------------------------------

/**
 * Parse a radio group requirement string.
 *
 * Radio groups are binary: require_1 (required) or require_0-1 (optional).
 * Canonical values are matched first by string equality. Non-canonical but
 * structurally valid strings (e.g. require_1-1, require_2) fall back to
 * regex extraction with min === 1 mapping to required (matching the old
 * `checked={requirement.min === 1}` behavior). Any other min value maps
 * to optional, preserving the exact toggle state legacy data had before
 * this refactor.
 * Returns null only for completely unparseable values.
 */
export function parseRadioRequirement(
  requirement: string | null | undefined,
): RadioGroupRequirement | null {
  if (requirement === 'require_1') return { required: true };
  if (requirement === 'require_0-1') return { required: false };
  if (requirement == null) return null;

  const match = requirement.match(GROUP_REQUIREMENT_PATTERN_STRICT);
  if (!match) return null;
  return { required: Number(match[1]) === 1 };
}

/**
 * Build a radio group requirement string.
 */
export function buildRadioRequirement(required: boolean): string {
  return required ? 'require_1' : 'require_0-1';
}
